import { useRef, useLayoutEffect } from 'react';
import styled from 'styled-components';

const TIME_VALUE = 0.002;
const ALPHA_VALUE = 255;
const COLORS = [
  [242, 92, 110],
  [255, 143, 81],
  [255, 222, 112],
  [198, 241, 109],
  [6, 152, 160],
  [176, 102, 255],
];

const interpolateColor = (
  color1: number[],
  color2: number[],
  factor: number
): number[] => [
  Math.round(color1[0] + (color2[0] - color1[0]) * factor),
  Math.round(color1[1] + (color2[1] - color1[1]) * factor),
  Math.round(color1[2] + (color2[2] - color1[2]) * factor),
];

const getWaveColor = (
  x: number,
  y: number,
  width: number,
  height: number,
  time: number
): number[] => {
  const wave1 = Math.cos((x / width) * Math.PI * 4 + time) * 0.3;
  const wave2 = Math.sin((y / height) * Math.PI * 2 + time * 0.7) * 0.25;
  const wave3 =
    Math.sin(((x + y) / (width + height)) * Math.PI * 3 + time * 1.2) * 0.4;
  const colorPosition = Math.max(
    0,
    Math.min(1, y / height + (wave1 + wave2 + wave3) * 0.3)
  );

  const scaledPos = colorPosition * (COLORS.length - 1);
  const colorIndex = Math.floor(scaledPos);

  if (colorIndex >= COLORS.length - 1) {
    return COLORS[COLORS.length - 1];
  }

  return interpolateColor(
    COLORS[colorIndex],
    COLORS[colorIndex + 1],
    scaledPos - colorIndex
  );
};

export const PrideGradient: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationRef = useRef<number | null>(null);
  const timeRef = useRef(0);

  const draw = (canvas: HTMLCanvasElement): void => {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    const width = canvas.offsetWidth;
    const height = canvas.offsetHeight;

    ctx.clearRect(0, 0, width, height);

    const imageData = ctx.createImageData(width, height);
    const data = imageData.data;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 2) {
        const color = getWaveColor(x, y, width, height, timeRef.current);

        for (let dy = 0; dy < 2 && y + dy < height; dy++) {
          for (let dx = 0; dx < 2 && x + dx < width; dx++) {
            // Pixels are stored in ImageData as a 1D array in which every
            // pixel takes 4 positions, each corresponding the the values of
            // rgba colors
            const pixelIndex = ((y + dy) * width + (x + dx)) * 4;
            data[pixelIndex] = color[0];
            data[pixelIndex + 1] = color[1];
            data[pixelIndex + 2] = color[2];
            data[pixelIndex + 3] = ALPHA_VALUE;
          }
        }
      }
    }

    ctx.putImageData(imageData, 0, 0);

    timeRef.current += TIME_VALUE;
    animationRef.current = requestAnimationFrame(() => {
      draw(canvas);
    });
  };

  const enter = (): number | null => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }

    const ref = requestAnimationFrame(() => {
      canvas.style.opacity = '1';
    });
    draw(canvas);
    return ref;
  };

  const resize = (): void => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const parentHeight = canvas.parentElement?.getBoundingClientRect()?.height;
    if (!parentHeight) {
      return;
    }

    canvas.width = window.innerWidth;
    canvas.height = parentHeight + 1; // Add missing pixel for some retina displays
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
  };

  useLayoutEffect(() => {
    resize();
    const entryAnimation = enter();

    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      if (entryAnimation !== null) {
        cancelAnimationFrame(entryAnimation);
      }

      if (animationRef.current !== null) {
        cancelAnimationFrame(animationRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Canvas ref={canvasRef} />;
};

const Canvas = styled.canvas`
  position: absolute;
  opacity: 0;
  inset: 0;
  transition: opacity 2s ease-out;
`;
